import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiX, FiLink, FiType, FiBox, FiServer, FiGlobe, FiFile } from "react-icons/fi";
import axios from "../../../utils/api/axios";
import { toast } from "react-hot-toast";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import usePrograms from "../../../utils/hooks/programs/usePrograms";

interface AddResourceModalProps {
    assetId: string;
    targetId: string;
    targetType: string;
    onClose: () => void;
    onSuccess: () => void;
}

const AddResourceModal: React.FC<AddResourceModalProps> = ({ assetId, targetId, targetType, onClose, onSuccess }) => {
    // Define mappings
    const RESOURCE_TYPE_OPTIONS = [
        { id: "PATH", icon: <FiLink />, label: "Path" },
        { id: "IP", icon: <FiGlobe />, label: "IP/CIDR" },
        { id: "PORT", icon: <FiServer />, label: "Port" },
        { id: "PARAMETER", icon: <FiType />, label: "Parameter" },
        { id: "HEADER", icon: <FiBox />, label: "Header" },
        { id: "FILE", icon: <FiFile />, label: "File" }
    ];

    const getAvailableTypes = () => {
        if (!targetType) return RESOURCE_TYPE_OPTIONS;

        const webTypes = ["PATH", "PARAMETER", "HEADER", "FILE"];
        const infraTypes = ["PORT", "PATH", "IP"];
        const networkTypes = ["IP", "PORT"];

        if (targetType.includes("Web URL") || targetType.includes("API Base") || targetType.includes("Webhook")) {
            return RESOURCE_TYPE_OPTIONS.filter(t => webTypes.includes(t.id));
        }
        if (targetType.includes("External IP") || targetType.includes("Host")) {
            return RESOURCE_TYPE_OPTIONS.filter(t => infraTypes.includes(t.id));
        }
        if (targetType.includes("CIDR") || targetType.includes("Network")) {
            return RESOURCE_TYPE_OPTIONS.filter(t => networkTypes.includes(t.id));
        }
        if (targetType.includes("Mobile App")) {
            return RESOURCE_TYPE_OPTIONS.filter(t => webTypes.includes(t.id));
        }

        return RESOURCE_TYPE_OPTIONS;
    };

    const availableTypes = getAvailableTypes();

    const [mode, setMode] = useState<"single" | "bulk">("single");
    const [resourceType, setResourceType] = useState<string>(availableTypes[0].id);
    const [value, setValue] = useState("");
    const [bulkValue, setBulkValue] = useState("");
    const [httpMethod, setHttpMethod] = useState("GET");
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("IN_SCOPE");
    const [programId, setProgramId] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { role: userRole } = useUserCredentials();
    const isAdminOrBusiness = userRole === UserRole.ADMIN || userRole === UserRole.BUSINESS || userRole === UserRole.SUB_ADMIN || userRole === UserRole.BUSINESS_ADMINISTRATOR;
    const isResearcher = userRole === UserRole.RESEARCHER;

    const { programs } = usePrograms();
    const activePrograms = (programs as any[])?.filter(p => p.phase_status === "In Progress" || p.phase_status === "Active") || [];

    const validateFormat = (type: string, val: string) => {
        if (!val) return false;
        switch (type) {
            case "IP":
                const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
                return ipRegex.test(val);
            case "PATH":
                return val.startsWith("/") || /^[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]+$/.test(val);
            case "PORT":
                const port = parseInt(val);
                return !isNaN(port) && port >= 1 && port <= 65535;
            default:
                return true;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (mode === "single") {
                if (!validateFormat(resourceType, value)) {
                    toast.error(`Invalid format for ${resourceType}`);
                    setLoading(false);
                    return;
                }
                if (isResearcher && !notes) {
                    toast.error("Notes are required for pentester discoveries.");
                    setLoading(false);
                    return;
                }
                if (isResearcher && !programId) {
                    toast.error("Please select the active program for this discovery.");
                    setLoading(false);
                    return;
                }

                await axios.post(`/assets/${assetId}/targets/${targetId}/resources`, {
                    value,
                    resource_type: resourceType,
                    http_method: resourceType === "PATH" ? httpMethod : null,
                    notes,
                    status: "IN_SCOPE",
                    program_id: programId ? parseInt(programId) : null
                });
                toast.success("Endpoint added successfully");
            } else {
                const lines = bulkValue.split("\n").map(l => l.trim()).filter(l => l !== "");
                const resources = lines.map(line => ({
                    value: line,
                    resource_type: resourceType,
                    http_method: resourceType === "PATH" ? httpMethod : null,
                    notes: "Bulk import"
                }));

                const invalid = resources.filter(r => !validateFormat(r.resource_type, r.value));
                if (invalid.length > 0) {
                    toast.error(`Contains ${invalid.length} invalid formats`);
                    setLoading(false);
                    return;
                }

                await axios.post(`/assets/${assetId}/targets/${targetId}/resources/bulk`, { resources });
                toast.success(`Imported ${resources.length} endpoints`);
            }
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Error adding resource:", error);
            toast.error(error.response?.data?.message || "Failed to add resource");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-[1.5rem] shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[85vh] border border-slate-200"
            >
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900 leading-tight">Add Endpoint</h3>
                        <p className="text-[11px] text-slate-400 font-medium">Define your testing scope</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors">
                        <FiX className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="p-5 space-y-5">
                        <div className="flex bg-slate-100/80 p-1 rounded-lg">
                            <button
                                type="button"
                                onClick={() => setMode("single")}
                                className={`flex-1 py-1.5 rounded-md text-[11px] font-bold transition-all ${mode === "single" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                Single Add
                            </button>
                            {isAdminOrBusiness && (
                                <button
                                    type="button"
                                    onClick={() => setMode("bulk")}
                                    className={`flex-1 py-1.5 rounded-md text-[11px] font-bold transition-all ${mode === "bulk" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                                >
                                    Bulk Import
                                </button>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 px-1">Resource Type</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {availableTypes.map((type) => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            onClick={() => setResourceType(type.id)}
                                            className={`flex flex-col items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl border-2 transition-all ${resourceType === type.id ? "border-blue-500 bg-blue-50/50 text-blue-700 shadow-sm shadow-blue-50" : "border-slate-100 text-slate-400 hover:border-slate-200"}`}
                                        >
                                            <span className="text-base">{type.icon}</span>
                                            <span className="text-[10px] font-bold uppercase tracking-tighter">{type.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {mode === "single" ? (
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 px-1">Value</label>
                                    <input
                                        type="text"
                                        value={value}
                                        onChange={(e) => setValue(e.target.value)}
                                        placeholder={resourceType === "PATH" ? "/api/v1/users" : resourceType === "IP" ? "192.168.1.1" : "8080"}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm font-medium"
                                        required
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 px-1">Bulk Values (line separated)</label>
                                    <textarea
                                        value={bulkValue}
                                        onChange={(e) => setBulkValue(e.target.value)}
                                        rows={4}
                                        placeholder={resourceType === "PATH" ? "/api/users\n/api/auth\n/api/v2/items" : "One value per line"}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-mono text-[11px]"
                                        required
                                    />
                                </div>
                            )}

                            {resourceType === "PATH" && (
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 px-1">HTTP Method</label>
                                    <select
                                        value={httpMethod}
                                        onChange={(e) => setHttpMethod(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 outline-none text-sm font-bold text-slate-700 cursor-pointer"
                                    >
                                        {["GET", "POST", "PUT", "DELETE", "PATCH", "HEAD", "OPTIONS"].map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {mode === "single" && (
                                <>
                                    <div className="space-y-1.5">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1 px-1">
                                            Scope Notes {isResearcher && <span className="text-red-500">*</span>}
                                        </label>
                                        <textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            rows={2}
                                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-xs font-medium resize-none shadow-sm"
                                            placeholder={isResearcher ? "Provide context about your discovery (Required)" : "Additional context..."}
                                            required={isResearcher}
                                        />
                                    </div>

                                    {isAdminOrBusiness && (
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 px-1">Initial Status</label>
                                            <select
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm font-bold text-slate-700"
                                            >
                                                <option value="IN_SCOPE">In Scope</option>
                                                <option value="OUT_OF_SCOPE">Out of Scope</option>
                                                <option value="EXCLUDED">Excluded</option>
                                            </select>
                                        </div>
                                    )}

                                    {isResearcher && (
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1.5 px-1">Link to Program <span className="text-red-500">*</span></label>
                                            <select
                                                value={programId}
                                                onChange={(e) => setProgramId(e.target.value)}
                                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm font-bold text-slate-700"
                                                required
                                            >
                                                <option value="">Select Active Program</option>
                                                {activePrograms.map((p: any) => (
                                                    <option key={p.program_id} value={p.program_id}>{p.program_title}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                    </div>

                    <div className="p-5 bg-slate-50 border-t border-slate-100 flex gap-3 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-white transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 px-4 py-2.5 rounded-xl font-bold text-white text-xs transition-all ${loading ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-100"}`}
                        >
                            {loading ? "Adding..." : mode === "single" ? "Add Resource" : "Import All"}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default AddResourceModal;
